import { missedDays, type Plan } from "@viamissa/planner";
import { googleMapsDirectionsUrl } from "@viamissa/routing";
import { useMemo, useState } from "react";
import { clock, dayClock, distance, formatDuration, KIND_LABEL, longDate } from "../lib/format";
import { waypointTimes, type TripResult } from "../lib/trip";
import { ChurchCard } from "./ChurchCard";

type Props = { result: TripResult; plan: Plan; onChoose: (plan: Plan) => void };

export function Itinerary({ result, plan, onChoose }: Props) {
  const home = result.originTz;
  const churchById = useMemo(() => new Map(result.churches.map((entry) => [entry.church.id, entry])), [result]);
  const eventById = useMemo(() => {
    const map = new Map<string, NonNullable<ReturnType<TripResult["events"]["get"]>>[number]>();
    for (const list of result.events.values()) for (const event of list) map.set(event.id, event);
    return map;
  }, [result]);

  const places = result.settings.places;
  const origin = places[0]!;
  const destination = places[places.length - 1]!;
  const arriveBy = result.constraint.kind === "arrive" ? result.constraint.by : null;
  // Churches and the user's own waypoints, in the order the route reaches them.
  const middle = [
    ...plan.stops.map((stop) => {
      const entry = churchById.get(stop.candidate.churchId)!;
      return { at: entry.join.alongMin, point: { lat: entry.church.lat, lng: entry.church.lng } };
    }),
    ...places.slice(1, -1).map((place, index) => ({
      at: result.route.cumMin[result.route.stopIndices[index + 1] ?? 0] ?? 0,
      point: place.point,
    })),
  ].sort((a, b) => a.at - b.at);
  const mapsUrl = googleMapsDirectionsUrl([origin.point, ...middle.map((entry) => entry.point), destination.point]);
  const avoid = result.settings.avoid;
  const avoidNames = [avoid.tolls && "tolls", avoid.highways && "highways", avoid.ferries && "ferries"].filter(Boolean);
  const alternatives = result.options.filter((option) => option.stops[0]?.candidate.id !== plan.stops[0]?.candidate.id);
  const [showAll, setShowAll] = useState(false);
  // Days of obligation this plan (not necessarily the first one offered) leaves without Mass.
  const missed = missedDays(plan, result.requiredDays);
  const massName = (candidateId: string) => {
    const event = eventById.get(candidateId);
    return event ? (event.occasion ? `${event.occasion} Mass` : KIND_LABEL[event.kind]) : "Mass";
  };
  // Why nothing fits, in the traveller's terms.
  const noneFits =
    result.churches.length === 0
      ? `No churches turned up within ${distance(result.settings.corridorKm)} of the route.`
      : result.constraint.kind === "depart"
        ? `None of the ${result.churches.length} churches along the way has a Mass you can reach leaving ${dayClock(result.constraint.at, home, home)}.`
        : `None of the ${result.churches.length} churches along the way has a Mass that still gets you there by ${dayClock(result.constraint.by, result.destinationTz, home)}.`;
  // The traveller's own stops, slotted between the Masses by when they are reached.
  const passes = waypointTimes(result, plan);
  const waypoints = places.slice(1, -1).map((place, index) => ({ label: place.label, time: passes[index] ?? plan.depart }));
  const waypointItem = (waypoint: (typeof waypoints)[number], index: number) => (
    <li key={`waypoint-${index}`}>
      <span className="time">{clock(waypoint.time, home, home)}</span>
      <span>Reach {waypoint.label}</span>
    </li>
  );

  return (
    <section className="itinerary">
      <div className="summary">
        <p className="lead">
          Leave <b>{dayClock(plan.depart, home, home)}</b>, arrive <b>{dayClock(plan.finish, result.destinationTz, home)}</b>
        </p>
        <p className="muted">
          {plan.stops.length === 0
            ? noneFits
            : `${plan.stops.length === 1 ? "One Mass" : `${plan.stops.length} Masses`} adds ${formatDuration(plan.addedMin * 60_000)} to the drive.`}
          {arriveBy !== null && plan.depart < result.direct.depart
            ? ` That means leaving ${formatDuration(result.direct.depart - plan.depart)} earlier than you otherwise would.`
            : ""}
          {plan.slackMin >= 10 && result.constraint.kind === "depart"
            ? ` You could leave up to ${formatDuration(plan.slackMin * 60_000)} later and still make it.`
            : ""}
        </p>
        {result.weekdayFallback && (
          <p className="muted">Your trip isn't on a Sunday or holy day, so this shows weekday Masses.</p>
        )}
        {result.nearby.length > 0 && (
          <div className="alternatives nearby">
            <h2>Closest times that work</h2>
            <ul>
              {result.nearby.map((option) => {
                const stop = option.stops[0]!;
                const entry = churchById.get(stop.candidate.churchId)!;
                const tz = result.timeZones.get(entry.church.id) ?? home;
                const chosen = plan.stops[0]?.candidate.id === stop.candidate.id;
                return (
                  <li key={stop.candidate.id}>
                    <div>
                      <b>{entry.church.name}</b>
                      <p className="muted small">
                        {massName(stop.candidate.id)} {dayClock(stop.candidate.start, tz, home)} · leave {dayClock(option.depart, home, home)} ·
                        arrive {dayClock(option.finish, result.destinationTz, home)}
                      </p>
                      {eventById.get(stop.candidate.id)?.comment && (
                        <p className="muted small">Parish note: {eventById.get(stop.candidate.id)!.comment}</p>
                      )}
                    </div>
                    <button type="button" className="small ghost" disabled={chosen} onClick={() => onChoose(option)}>
                      {chosen ? "Showing" : "Use this"}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {missed.length > 0 && (plan.stops.length > 0 || result.nearby.length === 0) && (
          <p className="warning">
            No Mass fits on {missed.map(longDate).join(", ")} within {result.settings.maxDetourMin} minutes of the route.
            Try a wider search, an earlier start, or{" "}
            {result.settings.drivingHours ? "longer driving hours" : "a different day"}.
          </p>
        )}
      </div>

      <ol className="timeline">
        <li>
          <span className="time">{clock(plan.depart, home, home)}</span>
          <span>Leave {origin.label}</span>
        </li>
        {plan.stops.map((stop, index) => {
          const before = waypoints
            .map((waypoint, at) => ({ waypoint, at }))
            .filter(({ waypoint }) => waypoint.time <= stop.arrive && (index === 0 || waypoint.time > plan.stops[index - 1]!.arrive));
          const entry = churchById.get(stop.candidate.churchId)!;
          const event = eventById.get(stop.candidate.id);
          const tz = result.timeZones.get(entry.church.id) ?? home;
          return [
            ...before.map(({ waypoint, at }) => waypointItem(waypoint, at)),
            <li key={stop.candidate.id} className="mass">
              <span className="time">{clock(stop.arrive, tz, home)}</span>
              <div>
                <p>
                  <b>{index + 1}.</b> Arrive at {entry.church.name}
                  {stop.waitMin >= 1 ? ` (${formatDuration(stop.waitMin * 60_000)} to spare)` : ""}
                </p>
                <p className="mass-line">
                  {massName(stop.candidate.id)} at <b>{clock(stop.candidate.start, tz, home)}</b>
                  {" · "}be seated by {clock(stop.seatedBy, tz, home)}, back on the road at {clock(stop.leave, tz, home)}
                </p>
                {event?.comment && <p className="small">Parish note: {event.comment}</p>}
                {event?.caveat && <p className="warning small">{event.caveat}. Call to confirm.</p>}
                <p className="muted small">
                  {Math.round(entry.toMin)} min off the route{entry.exact ? "" : " (estimated)"}
                </p>
                <ChurchCard church={entry.church} timeZone={tz} homeZone={home} />
              </div>
            </li>,
          ];
        })}
        {waypoints.map((waypoint, at) =>
          plan.stops.length === 0 || waypoint.time > plan.stops[plan.stops.length - 1]!.arrive ? waypointItem(waypoint, at) : null,
        )}
        <li>
          <span className="time">{clock(plan.finish, result.destinationTz, home)}</span>
          <span>Arrive at {destination.label}</span>
        </li>
      </ol>

      <div className="handoff">
        <a className="button" href={mapsUrl} target="_blank" rel="noopener noreferrer">
          Open in Google Maps
        </a>
        <p className="fine">
          Google Maps doesn't take a departure time from a link: set <b>Depart at {dayClock(plan.depart, home, home)}</b> there
          {avoidNames.length ? `, and turn on Avoid ${avoidNames.join(" and ")} under Options` : ""}.
          {!result.route.avoidHonoured ? " The backup router couldn't avoid those, so times here may be a little off." : ""}
        </p>
      </div>

      {alternatives.length > 0 && (
        <div className="alternatives">
          <h2>Other Masses on the way</h2>
          <ul>
            {(showAll ? alternatives : alternatives.slice(0, 3)).map((option) => {
              const stop = option.stops[0]!;
              const entry = churchById.get(stop.candidate.churchId)!;
              const tz = result.timeZones.get(entry.church.id) ?? home;
              return (
                <li key={stop.candidate.id}>
                  <div>
                    <b>{entry.church.name}</b>
                    <p className="muted small">
                      {dayClock(stop.candidate.start, tz, home)} · adds {formatDuration(option.addedMin * 60_000)} · arrive{" "}
                      {clock(option.finish, result.destinationTz, home)}
                      {result.constraint.kind === "arrive" ? ` · leave ${clock(option.depart, home, home)}` : ""}
                    </p>
                  </div>
                  <button type="button" className="small ghost" onClick={() => onChoose(option)}>
                    Use this
                  </button>
                </li>
              );
            })}
          </ul>
          {alternatives.length > 3 && !showAll && (
            <button type="button" className="link" onClick={() => setShowAll(true)}>
              Show {alternatives.length - 3} more
            </button>
          )}
        </div>
      )}
    </section>
  );
}
